/**
 * Faz V U8.5: Browser-side hata yakalama + backend upload. Operator
 * "docker logs photonremote-api | grep WEB-LOG" ile uzaktan SPA hatalarını
 * görebilir. Kullanıcıdan "F12 açıp console'u kopyala" istemeye gerek yok.
 *
 * Yakalanan:
 *   - window.onerror (sync JS exception)
 *   - window.unhandledrejection (async Promise fail)
 *   - logEvent() public helper — manuel önemli olayları yollar (bağlantı
 *     kurtarma, reconnect, decoder fail, vs.)
 *
 * Rate limit: 5 saniyede max 3 upload (aynı hata spam'ını önle). Kuyruk
 * dolu ise yeni olaylar drop edilir.
 */
import { appState } from '../state';
const WINDOW_MS = 5000;
const MAX_IN_WINDOW = 3;
const sentTimestamps = [];
/**
 * B4 #11: Backend log'una giden mesaj/stack içinden hassas bilgileri redact et.
 * - JWT (eyJ...x.x.x)
 * - UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
 * - Bearer token
 * - 6 haneli pairing kodu (\b\d{6}\b)
 * Stack trace de aynı pattern'lere maruz kalabilir; ikisi için de uygula.
 */
const RE_JWT = /eyJ[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]+/g;
const RE_UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const RE_BEARER = /Bearer\s+[A-Za-z0-9._\-]+/gi;
const RE_PAIRING = /\b\d{6}\b/g;
function sanitize(s) {
    if (!s)
        return s;
    return s
        .replace(RE_JWT, '<JWT>')
        .replace(RE_BEARER, 'Bearer <…>')
        .replace(RE_UUID, '<uuid>')
        .replace(RE_PAIRING, '<code>');
}
function canSend() {
    const now = Date.now();
    while (sentTimestamps.length > 0 && now - sentTimestamps[0] > WINDOW_MS) {
        sentTimestamps.shift();
    }
    return sentTimestamps.length < MAX_IN_WINDOW;
}
async function post(body) {
    const jwt = appState.accessToken;
    if (!jwt)
        return; // Login öncesi hataları atla (mükerrer token yok)
    if (!canSend())
        return;
    sentTimestamps.push(Date.now());
    // B4 #11: hassas bilgileri redact (mesaj + stack).
    body = { ...body, message: sanitize(body.message), stack: sanitize(body.stack) };
    try {
        await fetch(`${appState.backendUrl}/diagnostics/web-log`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jwt}`,
            },
            body: JSON.stringify(body),
            keepalive: true, // sayfa kapatılırken de gönderilsin
        });
    }
    catch {
        // Network fail sessiz — backend erişilemiyorsa zaten kullanıcının büyük
        // sorunu var, upload'u tekrar denemenin anlamı yok.
    }
}
/** Public API: kritik bir olay log'u manuel yolla. */
export function logEvent(level, message, stack) {
    void post({
        userAgent: navigator.userAgent,
        page: window.location.pathname + window.location.hash,
        level,
        message,
        stack,
    });
}
/** Global yakalayıcıları kur. main.ts init'te bir kez çağrılır. */
export function installGlobalErrorHandlers() {
    window.addEventListener('error', (ev) => {
        const msg = ev.message || (ev.error && String(ev.error)) || 'window.error';
        const stack = ev.error && ev.error.stack ? String(ev.error.stack) : undefined;
        void post({
            userAgent: navigator.userAgent,
            page: window.location.pathname + window.location.hash,
            level: 'error',
            message: `${msg} @ ${ev.filename ?? '?'}:${ev.lineno ?? 0}:${ev.colno ?? 0}`,
            stack,
        });
    });
    window.addEventListener('unhandledrejection', (ev) => {
        const reason = ev.reason;
        const msg = reason instanceof Error
            ? `unhandledrejection: ${reason.message}`
            : `unhandledrejection: ${String(reason)}`;
        const stack = reason instanceof Error ? reason.stack : undefined;
        void post({
            userAgent: navigator.userAgent,
            page: window.location.pathname + window.location.hash,
            level: 'error',
            message: msg,
            stack,
        });
    });
}
